// Futuristic UI primitives for the Fatigue Management dashboard.
//
// These intentionally lean on gradients, glass surfaces, soft glows and motion
// to give the section a forward-looking, "command-centre" feel while staying on
// the PEAK4 brand palette and MUI theme tokens.

import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Stack,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material";
import { brandColors } from "../../../../../theme/brandColors";
import type { RiskTier, TrendDirection } from "../data/mockData";

// ── Risk tier colour mapping ────────────────────────────────────────────────

export const tierColor = (tier: RiskTier, theme: Theme): string => {
  switch (tier) {
    case "green":
      return theme.palette.success.main;
    case "amber":
      return theme.palette.warning.main;
    case "red":
      return theme.palette.error.main;
  }
};

export const tierLabel = (tier: RiskTier): string =>
  tier === "green" ? "Green" : tier === "amber" ? "Amber" : "Red";

// ── Animations ──────────────────────────────────────────────────────────────

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; opacity: 0.9; }
  70% { box-shadow: 0 0 0 8px transparent; opacity: 1; }
  100% { box-shadow: 0 0 0 0 transparent; opacity: 0.9; }
`;

const sweep = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
`;

// ── GlassCard ───────────────────────────────────────────────────────────────
// Frosted, gradient-bordered surface used across all three layers.

interface GlassCardProps {
  children: React.ReactNode;
  accent?: string;
  glow?: boolean;
  sx?: React.ComponentProps<typeof Card>["sx"];
}

export const GlassCard: React.FC<GlassCardProps> = ({
  children,
  accent,
  glow = false,
  sx,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accentColor = accent ?? theme.palette.primary.main;

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        height: "100%",
        overflow: "hidden",
        borderRadius: 3,
        p: 2.5,
        backdropFilter: "blur(14px)",
        background: isDark
          ? `linear-gradient(160deg, ${alpha("#FFFFFF", 0.06)} 0%, ${alpha(
              "#FFFFFF",
              0.02
            )} 100%)`
          : `linear-gradient(160deg, ${alpha("#FFFFFF", 0.9)} 0%, ${alpha(
              accentColor,
              0.04
            )} 100%)`,
        border: `1px solid ${alpha(accentColor, isDark ? 0.25 : 0.18)}`,
        boxShadow: glow
          ? `0 0 0 1px ${alpha(accentColor, 0.15)}, 0 12px 40px -12px ${alpha(
              accentColor,
              0.5
            )}`
          : `0 8px 30px -16px ${alpha(theme.palette.common.black, 0.4)}`,
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 0 0 1px ${alpha(accentColor, 0.25)}, 0 18px 48px -14px ${alpha(
            accentColor,
            0.55
          )}`,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          insetInline: 0,
          top: 0,
          height: 3,
          background: `linear-gradient(90deg, ${accentColor}, ${alpha(
            accentColor,
            0
          )})`,
        },
        ...sx,
      }}
    >
      {children}
    </Card>
  );
};

// ── AnimatedCounter ─────────────────────────────────────────────────────────
// Eases a number up from 0 on mount. Respects integers vs decimals.

interface AnimatedCounterProps {
  value: number;
  decimals?: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
}

export const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  value,
  decimals = 0,
  duration = 1100,
  suffix = "",
  prefix = "",
}) => {
  const [display, setDisplay] = useState(0);
  const frame = useRef<number>(0);

  useEffect(() => {
    let start: number | null = null;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(value * eased);
      if (progress < 1) {
        frame.current = requestAnimationFrame(step);
      }
    };
    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [value, duration]);

  return (
    <>
      {prefix}
      {display.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </>
  );
};

// ── TrendChip ───────────────────────────────────────────────────────────────

interface TrendChipProps {
  trend: TrendDirection;
  label: string;
  goodDirection: "up" | "down";
}

export const TrendChip: React.FC<TrendChipProps> = ({
  trend,
  label,
  goodDirection,
}) => {
  const theme = useTheme();
  const isGood =
    trend === "stable"
      ? null
      : (trend === "up" && goodDirection === "up") ||
        (trend === "down" && goodDirection === "down");
  const color =
    isGood === null
      ? theme.palette.text.secondary
      : isGood
      ? theme.palette.success.main
      : theme.palette.error.main;
  const arrow = trend === "up" ? "▲" : trend === "down" ? "▼" : "▬";

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        color,
        bgcolor: alpha(color, 0.12),
      }}
    >
      <Box component="span" sx={{ fontSize: 9 }}>
        {arrow}
      </Box>
      {label}
    </Box>
  );
};

// ── MetricRing ──────────────────────────────────────────────────────────────
// SVG conic-style progress ring with a glow, animated stroke.

interface MetricRingProps {
  value: number; // 0–100 fill
  display: string; // big centre text
  caption?: string;
  color: string;
  size?: number;
}

export const MetricRing: React.FC<MetricRingProps> = ({
  value,
  display,
  caption,
  color,
  size = 132,
}) => {
  const theme = useTheme();
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const [offset, setOffset] = useState(circumference);

  useEffect(() => {
    const t = setTimeout(
      () => setOffset(circumference * (1 - Math.min(value, 100) / 100)),
      80
    );
    return () => clearTimeout(t);
  }, [value, circumference]);

  const gradId = `ring-${color.replace("#", "")}`;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size}>
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={alpha(color, 0.4)} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={alpha(theme.palette.text.primary, 0.08)}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: "stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1)",
            filter: `drop-shadow(0 0 6px ${alpha(color, 0.6)})`,
          }}
        />
      </svg>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ position: "absolute", inset: 0 }}
      >
        <Typography variant="h4" fontWeight={800} lineHeight={1}>
          {display}
        </Typography>
        {caption && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, textAlign: "center", px: 1 }}
          >
            {caption}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

// ── TierDot ─────────────────────────────────────────────────────────────────

export const TierDot: React.FC<{ tier: RiskTier; pulse?: boolean; size?: number }> = ({
  tier,
  pulse: doPulse = false,
  size = 12,
}) => {
  const theme = useTheme();
  const color = tierColor(tier, theme);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: color,
        color,
        flexShrink: 0,
        boxShadow: `0 0 8px ${alpha(color, 0.8)}`,
        animation: doPulse ? `${pulse} 1.8s infinite` : "none",
      }}
    />
  );
};

// ── TrafficLightCell ────────────────────────────────────────────────────────
// One square in the LRM grid.

export const TrafficLightCell: React.FC<{ tier: RiskTier; letter?: boolean }> = ({
  tier,
  letter = false,
}) => {
  const theme = useTheme();
  const color = tierColor(tier, theme);
  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 800,
        color: theme.palette.getContrastText(color),
        background: `linear-gradient(150deg, ${color}, ${alpha(color, 0.75)})`,
        boxShadow: `0 0 10px ${alpha(color, 0.45)}`,
      }}
    >
      {letter ? tierLabel(tier).charAt(0) : ""}
    </Box>
  );
};

// ── SectionHeading ──────────────────────────────────────────────────────────

export const SectionHeading: React.FC<{
  overline?: string;
  title: string;
  subtitle?: string;
}> = ({ overline, title, subtitle }) => (
  <Stack spacing={0.5} sx={{ mb: 1 }}>
    {overline && (
      <Typography
        variant="overline"
        sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 1.5 }}
      >
        {overline}
      </Typography>
    )}
    <Typography variant="h5" fontWeight={800}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Stack>
);

// ── ScanlineBanner ──────────────────────────────────────────────────────────
// Eye-catching alert strip with an animated light sweep.

export const ScanlineBanner: React.FC<{
  tier: RiskTier;
  title: string;
  body: string;
}> = ({ tier, title, body }) => {
  const theme = useTheme();
  const color = tierColor(tier, theme);
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 3,
        p: 2.5,
        border: `1px solid ${alpha(color, 0.5)}`,
        background: `linear-gradient(120deg, ${alpha(color, 0.18)}, ${alpha(
          color,
          0.04
        )})`,
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "40%",
          height: "100%",
          background: `linear-gradient(90deg, transparent, ${alpha(
            "#FFFFFF",
            0.12
          )}, transparent)`,
          animation: `${sweep} 4s linear infinite`,
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <TierDot tier={tier} pulse size={14} />
        <Box>
          <Typography variant="subtitle1" fontWeight={800} sx={{ color }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {body}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

// Brand accents re-exported for convenience in the views.
export const METRIC_ACCENTS = {
  fed: brandColors.recoverPink,
  lrm: brandColors.performOrange,
  cdi: brandColors.fuelIndigo,
  pdc: brandColors.focusLilac,
} as const;

// ── BulletChart ─────────────────────────────────────────────────────────────
// Actual vs target with qualitative risk bands. The exec-grade "progress vs
// goal" graphic (DataCamp best-practice for strategic dashboards).

interface BulletChartProps {
  value: number;
  target: number;
  max?: number;
  // qualitative bands as [end%] cut points, low→high (e.g. red ≤35, amber ≤65)
  bands?: { red: number; amber: number };
  unit?: string;
  // when true, higher value is good (readiness); shading inverts
  higherIsBetter?: boolean;
}

export const BulletChart: React.FC<BulletChartProps> = ({
  value,
  target,
  max = 100,
  bands = { red: 50, amber: 75 },
  higherIsBetter = true,
}) => {
  const theme = useTheme();
  const pct = (n: number) => `${Math.min((n / max) * 100, 100)}%`;
  const valueTier: RiskTier = higherIsBetter
    ? value < bands.red
      ? "red"
      : value < bands.amber
      ? "amber"
      : "green"
    : value > bands.amber
    ? "red"
    : value > bands.red
    ? "amber"
    : "green";
  const valueColor = tierColor(valueTier, theme);

  // band segment colours along the track
  const lowColor = higherIsBetter
    ? theme.palette.error.main
    : theme.palette.success.main;
  const highColor = higherIsBetter
    ? theme.palette.success.main
    : theme.palette.error.main;

  return (
    <Box sx={{ position: "relative", width: "100%", py: 2 }}>
      {/* qualitative bands */}
      <Box
        sx={{
          position: "relative",
          height: 22,
          borderRadius: 999,
          overflow: "hidden",
          display: "flex",
        }}
      >
        <Box sx={{ width: pct(bands.red), bgcolor: alpha(lowColor, 0.28) }} />
        <Box
          sx={{
            width: pct(bands.amber - bands.red),
            bgcolor: alpha(theme.palette.warning.main, 0.28),
          }}
        />
        <Box
          sx={{ flex: 1, bgcolor: alpha(highColor, 0.28) }}
        />
      </Box>
      {/* value bar */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: "50%",
          transform: "translateY(-50%)",
          height: 10,
          width: pct(value),
          borderRadius: 999,
          background: `linear-gradient(90deg, ${alpha(valueColor, 0.7)}, ${valueColor})`,
          boxShadow: `0 0 10px ${alpha(valueColor, 0.7)}`,
          transition: "width 1.1s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
      {/* target marker */}
      <Box
        sx={{
          position: "absolute",
          left: pct(target),
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: 4,
          height: 30,
          borderRadius: 1,
          bgcolor: theme.palette.text.primary,
        }}
      />
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Typography variant="caption" sx={{ color: valueColor, fontWeight: 800 }}>
          Now {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Target {target}
        </Typography>
      </Stack>
    </Box>
  );
};

// ── Sparkline ───────────────────────────────────────────────────────────────
// Tiny inline trend, no axes. Used in tables and KPI cards.

export const Sparkline: React.FC<{
  values: number[];
  color?: string;
  width?: number;
  height?: number;
}> = ({ values, color, width = 90, height = 28 }) => {
  const theme = useTheme();
  const c = color ?? theme.palette.primary.main;
  if (!values.length) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / span) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      <polyline
        points={pts}
        fill="none"
        stroke={c}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={width}
        cy={height - ((values[values.length - 1] - min) / span) * height}
        r={2.5}
        fill={c}
      />
    </svg>
  );
};

// ── RosterHeatmap ───────────────────────────────────────────────────────────
// Predicted fatigue risk per team per upcoming shift. Bio-mathematical-model
// style — colour-banded so managers re-roster before the shift.

const riskBandColor = (score: number, theme: Theme): string => {
  if (score >= 70) return theme.palette.error.main;
  if (score >= 45) return theme.palette.warning.main;
  return theme.palette.success.main;
};

export const RosterCell: React.FC<{ score: number }> = ({ score }) => {
  const theme = useTheme();
  const color = riskBandColor(score, theme);
  return (
    <Box
      sx={{
        height: 42,
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 800,
        color: theme.palette.getContrastText(color),
        background: `linear-gradient(150deg, ${color}, ${alpha(color, 0.7)})`,
        boxShadow: `0 0 10px ${alpha(color, 0.4)}`,
        transition: "transform 0.15s ease",
        "&:hover": { transform: "scale(1.06)" },
      }}
    >
      {score}
    </Box>
  );
};

// ── PersonDial ──────────────────────────────────────────────────────────────
// Individual fatigue dial (0–100, higher = more fatigued).

export const PersonDial: React.FC<{ score: number; tier: RiskTier }> = ({
  score,
  tier,
}) => {
  const theme = useTheme();
  const color = tierColor(tier, theme);
  return (
    <MetricRing
      value={score}
      display={`${score}`}
      caption="Fatigue score"
      color={color}
      size={150}
    />
  );
};
